// BenefitsSection.jsx
import React, { useState } from 'react';
import { useApplication } from './hooks/useApplication';

const BenefitsSection = ({ applicationData, mode, applicationId }) => {
  const { verifyBenefit, downloadFile } = useApplication(applicationId);
  
  const [benefitComment, setBenefitComment] = useState(applicationData.benefitComment || '');
  const [loading, setLoading] = useState(false);

  const handleVerifyBenefit = async (benefitId) => {
    try {
      setLoading(true);
      await verifyBenefit(benefitId, benefitComment);
    } catch (error) {
      console.error('Error verifying benefit:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (document, part) => {
    try {
      await downloadFile(applicationData.id, document.id, part, document.originalName);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      verified: { class: 'status-verified', label: 'Підтверджено' },
      not_verified: { class: 'status-pending', label: 'Не підтверджено' }
    };
    
    const config = statusConfig[status] || statusConfig.not_verified;
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  const getDocumentIcon = (type) => {
    const icons = {
      pdf: '📄',
      jpg: '🖼️',
      png: '🖼️',
      zip: '📦',
      docx: '📝',
      xlsx: '📊'
    };
    return icons[type] || '📎';
  };

  const getBenefitStatusText = () => {
    return applicationData.benefitStatus === 'verified' 
      ? 'Підтверджена пільга' 
      : 'Не підтверджена пільга';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('uk-UA');
    } catch (error) {
      return dateString;
    }
  };

  const isHasBenDesc = applicationData.benefits?.filter(b=>b.id).length > 0 || false;

  if(!isHasBenDesc) return(
    <div className="data-row">
            <span className="data-value">Пільги відсутні</span>
    </div>
  );

  return (
    <div className="benefits-section">
      <h3 className="section-title">Пільги</h3>
      
      <div className="data-section">
        {applicationData.benefits?.filter(b=>b.id).map((benefit, index) => (
          <div key={benefit.id || index} className="data-row benefit-item">
            <div className="benefit-header">
              <label className="data-label">Пільга {index + 1}:</label>
              <span className="data-value benefit-name">
                {benefit.benefitName} 
                {benefit.approved && (
                  <span className="status-badge status-verified ms-2">
                    Підтверджено {benefit.approvedBy ? `(${benefit.approvedBy})` : ''}
                  </span>
                )}
              </span>
            </div>
            
            {/* Дополнительная информация о льготе */}
            <div className="benefit-details">
              <div className="benefit-detail-row">
                <span className="detail-label">Номер документу:</span>
                <span className="detail-value">{benefit.documentNumber}</span>
              </div>
              
              <div className="benefit-detail-row">
                <span className="detail-label">Тип документу:</span>
                <span className="detail-value">{benefit.documentType}</span>
              </div>
              
              {benefit.documentIssueDate && (
                <div className="benefit-detail-row">
                  <span className="detail-label">Дата видачі:</span>
                  <span className="detail-value">
                    {benefit.issueDateFormatted || formatDate(benefit.documentIssueDate)}
                  </span>
                </div>
              )}
              
              {benefit.documentTo && (
                <div className="benefit-detail-row">
                  <span className="detail-label">Документ на ім'я:</span>
                  <span className="detail-value">{benefit.documentTo}</span>
                </div>
              )}
              
              {benefit.kindergartenId && (
                <div className="benefit-detail-row">
                  <span className="detail-label">Дитячий заклад:</span>
                  <span className="detail-value">{benefit.kindergartenId}</span>
                </div>
              )}
              
              {/* Статус подтверждения */}
              <div className="benefit-detail-row">
                <span className="detail-label">Статус підтвердження:</span>
                <span className="detail-value">
                  {benefit.approved ? (
                    <span className="status-badge status-verified">
                      Підтверджено
                      {benefit.approvedBy && ` (${benefit.approvedBy})`}
                    </span>
                  ) : (
                    <span className="status-badge status-pending">Очікує підтвердження</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        ))}
        
        {(!applicationData.benefits || applicationData.benefits.filter(b=>b.id).length === 0) && (
          <div className="data-row">
            <span className="data-value">Пільги відсутні</span>
          </div>
        )}
        
        <div className="data-row">
          <label className="data-label">Загальний статус пільг:</label>
          <div className="status-section">
            {getStatusBadge(applicationData.benefitStatus)}
            <span className="status-text">{getBenefitStatusText()}</span>
          </div>
        </div>
      </div>

      {/* Документы для льгот */}
      <div className="documents-section">
        <h4 className="documents-title">Документи для пільги</h4>
        <div className="documents-list">
          {applicationData.benefitDocuments?.map(doc => (
            <div key={doc.id} className="document-item">
              <div className="document-info">
                <span className="document-icon">{getDocumentIcon(doc.type)}</span>
                <div className="document-details">
                  <div className="document-name">{doc.name}</div>
                  <div className="document-meta">
                    <span className="document-type">{doc.type}</span>
                    <span className="document-size">{doc.size}</span>
                    <span className="document-date">{doc.uploadedAt}</span>
                  </div>
                </div>
              </div>
              <button 
                className="btn-download"
                onClick={() => handleDownload(doc, "benefits")}
                title="Скачати документ"
                disabled={loading}
              >
                ⬇️
              </button>
            </div>
          ))}
          {(!applicationData.benefitDocuments || applicationData.benefitDocuments.length === 0) && (
            <div className="no-documents">Документи відсутні</div>
          )}
        </div>
      </div>

      {/* Комментарий и действия для льгот */}
      {mode === 'edit' && (
        <div className="actions-section">
          <div className="comment-field">
            <label htmlFor="benefit-comment" className="comment-label">
              Коментар до пільги:
            </label>
            <textarea
              id="benefit-comment"
              className="form-control comment-textarea"
              value={benefitComment}
              onChange={(e) => setBenefitComment(e.target.value)}
              placeholder="Введіть коментар до пільги..."
              rows="3"
            />
          </div>
          
          <div className="action-buttons">
            {applicationData.benefits?.map((benefit, index) => (
              <button 
                key={benefit.id || index}
                className="btn btn-success"
                onClick={() => handleVerifyBenefit(benefit.id)}
                disabled={benefit.approved || loading}
              >
                {loading ? 'Обробка...' : `Підтвердити пільгу ${index + 1}`}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BenefitsSection;